import { CheckIcon, CopyIcon } from '@phosphor-icons/react'
import classNames from 'classnames'
import { useEffect, useState } from 'react'
import invariant from 'tiny-invariant'
import z from 'zod'
import { TooltipWithHover } from '../tooltip.tsx'
import * as styles from './copy-button.css.ts'

export const CopyButtonSideSchema = z.enum(['top', 'right', 'bottom', 'left'])
export type CopyButtonSide = z.infer<typeof CopyButtonSideSchema>

export const CopyButtonSizeSchema = z.enum(['small', 'medium', 'large', 'xlarge'])
export type CopyButtonSize = z.infer<typeof CopyButtonSizeSchema>

/**
 * How long the button reads as copied. Exported because a caller that announces the copy itself has
 * to clear its announcement when the check mark clears — leaving the two to drift would have a
 * screen reader saying "Copied" after the button had stopped saying it.
 */
export const COPIED_FEEDBACK_MS = 1600

const SIZE_TO_PIXELS_MAP: Record<CopyButtonSize, number> = {
  small: 12,
  medium: 16,
  large: 20,
  xlarge: 24,
}

type CopyButtonProps = {
  copyText: string
  tooltipText?: string
  side?: CopyButtonSide
  label: string
  className?: string
  size?: CopyButtonSize
  /** Called after the text reaches the clipboard, for callers that announce the copy themselves. */
  onCopy?: () => unknown
}

export const CopyButton: React.FC<CopyButtonProps> = ({
  copyText,
  tooltipText,
  label,
  className,
  side = 'top',
  size = 'small',
  onCopy,
}) => {
  const sizeInPixels = SIZE_TO_PIXELS_MAP[size]
  invariant(sizeInPixels)

  const [copied, setCopied] = useState(false)

  useEffect(() => {
    let timeout: NodeJS.Timeout
    if (copied) {
      timeout = setTimeout(() => {
        setCopied(false)
      }, COPIED_FEEDBACK_MS)
    }
    return () => clearTimeout(timeout)
  }, [copied])

  const handleCopy = () => {
    // Nothing reports a copy until the write lands. `writeText` rejects on a denied permission, and
    // reporting success anyway would show the check mark — and have `onCopy` announce "Copied" to a
    // screen reader — for a clipboard that still holds whatever it held before. The rejection is
    // swallowed rather than surfaced: the value is still on screen and selectable, so the user has
    // lost nothing they cannot recover by hand.
    void navigator.clipboard
      .writeText(copyText)
      .then(() => {
        setCopied(true)
        onCopy?.()
      })
      .catch(() => undefined)
  }

  if (tooltipText) {
    return (
      // gives the button width or else absolute placed icons give it a width of 0
      <div style={{ width: `${sizeInPixels}px`, height: `${sizeInPixels}px` }}>
        <TooltipWithHover
          trigger={
            <button
              className={classNames(styles.copyButton, className)}
              onClick={handleCopy}
              aria-label={label}
              style={{ width: sizeInPixels, height: sizeInPixels }}
            >
              <CopyIcon
                className={classNames(styles.icon, styles.copyIcon, { [styles.hidden]: copied })}
                size={sizeInPixels}
                weight="light"
              />
              <CheckIcon
                className={classNames(styles.icon, styles.checkIcon, { [styles.visible]: copied })}
                data-testid="check-icon"
                size={sizeInPixels}
                weight="regular"
              />
            </button>
          }
          side={side}
        >
          {tooltipText}
        </TooltipWithHover>
      </div>
    )
  }

  return (
    <button
      className={classNames(styles.copyButton, className)}
      onClick={handleCopy}
      aria-label={label}
      style={{ width: sizeInPixels, height: sizeInPixels }}
    >
      <CopyIcon
        className={classNames(styles.icon, styles.copyIcon, { [styles.hidden]: copied })}
        size={sizeInPixels}
        weight="light"
      />
      <CheckIcon
        className={classNames(styles.icon, styles.checkIcon, { [styles.visible]: copied })}
        data-testid="check-icon"
        size={sizeInPixels}
        weight="regular"
      />
    </button>
  )
}
